import OutagePageIframe from "@componentsOutagePageIframe";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import {
	desktopLMediaQuery,
	desktopMediaQuery,
	device,
	filterUrlPath,
	isAnchorOrDocumentLink,
	isFelixMobileUrl,
	laptopMediaQuery,
	mobileMediaQuery,
	richTextHelper,
	tabletMediaQuery,
} from "@helpers";
import { mixpanelTrack } from "@helpers/mixpanel.helper";
import type { IModuleText } from "@types-contentful";
import { textToId } from "@utilstextToId";
import Link from "next/link";
import { useRouter } from "next/router";
import type React from "react";
import styled from "styled-components";
import NetworkPageIframe from "./NetworkPageIframe";
import DfsvForm from "./DfsvForm";

const StyledCardWrapper = styled.div<{
	resetModuleMarginBottom: string;
}>`
  letter-spacing: normal;

  color: #121252;
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  
  ${({ resetModuleMarginBottom }) =>
		resetModuleMarginBottom === "Yes" && "margin-bottom: 0px !important"};
`;

const StyleInnerContent = styled.div`
  a {
  }
  padding: 0px 39px;

  @media (min-width: ${device.tablet}) and (max-width: ${device.laptopM}) {
    padding: 0px 64px 0px;
  }

  @media (min-width: ${device.laptop}) and (max-width: ${device.desktopS}) {
    padding: 0px 166px 0px;
  }

  @media (min-width: ${device.desktopS}) and (max-width: ${device.desktop}) {
    padding: 0px 241px 0px;
  }

  @media (min-width: ${device.desktop}) {
    padding: 0px 561px 0px;
  }

  > h1,
  h2,
  h3,
  h5,
  h6 {
    padding-bottom: 2%;
  }

  > h4 {
    padding-bottom: 20px;
    @media (min-width: ${device.tablet}) and (max-width: ${device.laptopM}) {
      padding-bottom: 30px;
    }

    @media (min-width: ${device.laptop}) and (max-width: ${device.desktopS}) {
      padding-bottom: 40px;
    }

    @media (min-width: ${device.desktopS}) and (max-width: ${device.desktop}) {
      padding-bottom: 40px;
    }

    @media (min-width: ${device.desktop}) {
      padding-bottom: 40px;
    }
  }

  > div {
    margin: 0px;
    padding: 0px;
  }
`;

const StyleParagraph = styled.p`
  padding-bottom: 1%;
`;

const StyleLinkContentText = styled.a`
  color: #121252;

  @media (min-width: ${device.tablet}) and (max-width: ${device.tabletL}) {
  }
`;

const SVGImage = styled.img`
  width: 100%;

  ${mobileMediaQuery} {
    max-height: 200px;
    height: 200px;
  }

  ${tabletMediaQuery} {
    max-height: 300px;
    height: 300px;
  }

  ${laptopMediaQuery} {
    max-height: 350px;
    height: 350px;
  }

  ${desktopMediaQuery} {
    max-height: 450px;
    height: 450px;
  }

  ${desktopLMediaQuery} {
    max-height: 500px;
    height: 500px;
  }
`;
type ContentfulModuleTextProps = IModuleText;

const ContentfulModuleText: React.FC<ContentfulModuleTextProps> = ({
	fields,
	sys,
}) => {
	const { bodyContent, removeNegativeSpacingBelowModule } = fields;
	const { pathname } = useRouter();

	const options = {
		renderNode: {
			[BLOCKS.PARAGRAPH]: (node, children) => (
				<StyleParagraph>{children}</StyleParagraph>
			),
			[INLINES.HYPERLINK]: (node, children) =>
				isAnchorOrDocumentLink(node.data.uri) ? (
					<a
						onClick={() =>
							mixpanelTrack("text_link_clicked", {
								cta_position: pathname,
								cta_text: children["0"]["1"],
							})
						}
						href={node.data.uri}
					>
						{children}
					</a>
				) : (
					<Link
						onClick={() =>
							mixpanelTrack("text_link_clicked", {
								cta_position: pathname,
								cta_text: children["0"]["1"],
							})
						}
						href={
							node.data && isFelixMobileUrl(node.data.uri)
								? filterUrlPath(node.data.uri)
								: node.data.uri
						}
					>
						{children}
					</Link>
				),
			[INLINES.ASSET_HYPERLINK]: (node, children) => {
				return (
					<Link
						href={
							node.data &&
							node.data.target &&
							node.data.target.fields &&
							node.data.target.fields.file["en-US"].url
								? node.data.target.fields.file["en-US"].url
								: null
						}
					>
						<StyleLinkContentText>{children}</StyleLinkContentText>
					</Link>
				);
			},
			[INLINES.ENTRY_HYPERLINK]: (node, children) => {
				const pageLink =
					node.data && node.data?.target && node.data?.target?.relativeUrl;

				if (pageLink) {
					return <Link href={pageLink}>{children}</Link>;
				}
				return <span>{children}</span>;
			},
		},
	};

	return sys.id === "1UJ811toMe58IRi46b3S4H" ||
		sys.id === "5wMRiA4a6etDpWrVwAq5So" ? (
		<NetworkPageIframe />
	) : sys.id === "6tqOShrquRE7kFYlL1un5v" ? (
		<OutagePageIframe />
	) : sys.id === "1G1KphjfeMnMGSt9ba0omu" ? (
		<DfsvForm />
	) : (
		<StyledCardWrapper
			id={fields.name ? textToId(fields.name) : undefined}
			resetModuleMarginBottom={removeNegativeSpacingBelowModule}
		>
			<StyleInnerContent>
				{richTextHelper(bodyContent && bodyContent, options)}{" "}
			</StyleInnerContent>
		</StyledCardWrapper>
	);
};

export default ContentfulModuleText;
